import {
  Avatar,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  TextField,
  Chip,
  Box,
} from "@mui/material";

import { ChangeEvent, KeyboardEvent, useState } from "react";
import { Client } from "typesense";
import { SearchResponse } from "typesense/lib/Typesense/Documents";
import Iconify from "~src/components/Iconify";
import { BestSellingProductsFilters } from "~src/pages/winningProducts/Products";

const SEARCH_KEY = import.meta.env.VITE_SEARCH_KEY || "";
const SEARCH_NODE = import.meta.env.VITE_SEARCH_NODE || "";

const client = new Client({
  nodes: [
    {
      host: SEARCH_NODE,
      port: 443,
      protocol: "https",
    },
  ],
  apiKey: SEARCH_KEY,
  cacheSearchResultsForSeconds: 2 * 60,
});

interface SearchBarProps {
  selected: BrandResult[];
  setSelected: React.Dispatch<React.SetStateAction<BrandResult[]>>;
  onSelect: (brand_ids: string) => void;
  localFilters: BestSellingProductsFilters | undefined;
  removeBrandId: (brand_id: string) => void;
}

export type BrandResult = {
  id: string;
  brand_name: string;
  description?: string;
  image?: string;
};

export const SearchBar: React.FC<SearchBarProps> = ({
  selected,
  setSelected,
  onSelect,
  localFilters,
  removeBrandId,
}) => {
  const [searchText, setSearchText] = useState("");
  const [showClearButton, setShowClearButton] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);

  const [autocompleteResults, setAutocompleteResults] =
    useState<SearchResponse<BrandResult>>();

  const handleSearch = () => {
    if (searchText.length >= 4) {
      setShowClearButton(false);
    }
  };

  const handleClear = () => {
    setSearchText("");
    setShowClearButton(false);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchText(value);

    if (value.length < 2) {
      setShowClearButton(false);
      setAutocompleteResults(undefined);
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
      autocomplete(value);
    }
  };

  const autocomplete = async (searchText: string) => {
    const results = await client
      .collections<BrandResult>("brands")
      .documents()
      .search({
        q: searchText,
        query_by: ["brand_name", "brand_url"],
      });
    setAutocompleteResults(results);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleBrandSelect = (brand: BrandResult) => {
    setSearchText("");
    setAnchorEl(null);
    setSelected((previousBrands) => {
      const brandExists = previousBrands.some((b) => b.id === brand.id);
      if (brandExists) {
        return previousBrands;
      } else {
        onSelect(brand.id);
        return [...previousBrands, brand];
      }
    });
  };

  const handleBrandDeselect = (brandId: string) => {
    setSelected((previousBrands) => {
      return previousBrands.filter((brand) => brand.id !== brandId);
    });
    removeBrandId(brandId);
  };

  return (
    <Box>
      <TextField
        placeholder="Search"
        fullWidth
        value={searchText}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        slotProps={{
          input: {
            style: { height: 45 },
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <Iconify icon="iconamoon:search" />
                </InputAdornment>
              </>
            ),
            endAdornment: (
              <>
                {showClearButton && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClear}>
                      <Iconify icon="uil:times-circle" />
                    </IconButton>
                  </InputAdornment>
                )}
              </>
            ),
          }
        }}
      />
      {localFilters &&
        // selected.length > 0 &&
        selected.map((item) => (
          <Chip
            label={item.brand_name}
            sx={{ mt: 1, mr: 1 }}
            onDelete={() => handleBrandDeselect(item.id)}
          />
        ))}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <List>
          {autocompleteResults?.hits?.map((result) => (
            <ListItem
              sx={{
                cursor: "pointer",
                color: "inherit",
                textDecoration: "none",
              }}
              onClick={() => handleBrandSelect(result.document)}
              key={result.document.id}
            >
              <ListItemAvatar>
                <Avatar src={result.document.image} />
              </ListItemAvatar>
              <ListItemText primary={result.document.brand_name} />
            </ListItem>
          ))}
        </List>
      </Popover>
    </Box>
  );
};
